/**
 * 기업 로고 (화면 공용)
 * ---------------------------------------------------------------------------
 * 로그인한 기업의 로고를 한 번만 받아 두고, 그것을 쓰는 모든 자리가 같은
 * 그림을 본다. 지금은 머리말 한 곳이지만, 로고를 바꾸는 자리(기업 설정)와
 * 보여 주는 자리(머리말)가 서로 다른 화면이라 둘을 잇는 끈이 필요하다.
 *
 * 왜 React Query 가 아니라 모듈 상태인가
 *   값이 blob URL 이다. 캐시가 조용히 버리면 URL.revokeObjectURL 을 부를
 *   자리가 없어 메모리에 남는다. 여기서 바꿀 때마다 옛 URL 을 직접 놓아
 *   준다 — support-mode 와 같은 모양이다.
 *
 * 왜 <img src="/api/tenant/logo"> 가 아닌가
 *   Access Token 은 메모리에 있고 요청 헤더로 간다. <img> 는 헤더를 붙일
 *   수 없어 비로그인으로 불리고, 토큰을 주소에 담으면 브라우저 기록에
 *   남는다. 사진·서명(UserImageModal)과 같은 이유다.
 */

import { apiClient } from './api-client';

let current: string | null = null;
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function replace(next: string | null): void {
  if (current === next) return;
  if (current) URL.revokeObjectURL(current);
  current = next;
  for (const listener of listeners) listener();
}

export function getCompanyLogo(): string | null {
  return current;
}

export function subscribeCompanyLogo(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * 서버에서 다시 받아 온다.
 *
 * 등록하지 않았으면 서버가 204 를 준다 — 오류가 아니다. 그때 화면은
 * 제품 로고만 보여 준다.
 *
 * 같은 순간에 여러 화면이 불러도 요청은 한 번만 나간다. 머리말과 설정
 * 화면이 함께 뜨는 경우가 있다.
 */
export function loadCompanyLogo(): Promise<void> {
  inFlight ??= (async () => {
    try {
      const res = await apiClient.get('/tenant/logo', { responseType: 'blob' });
      const blob = res.data as Blob | undefined;
      replace(blob && blob.size > 0 ? URL.createObjectURL(blob) : null);
    } catch {
      // 받지 못한 것으로 머리말이 깨지면 안 된다. 제품 로고만 남는다.
      replace(null);
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/** 로그아웃·지원 모드 전환처럼 보던 기업이 바뀔 때. */
export function clearCompanyLogo(): void {
  replace(null);
}
